import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

// A phone's width (iPhone 14/15): wider spaces, like the AI draft modal,
// show the page in a column this wide, as on a phone
const PHONE_WIDTH = 390;

// As the app's web view: images and embeds never wider than the screen,
// links opened outside (here in a new tab), and edge to edge when full width
const BASE_STYLE = `
  img, video, iframe, embed, object { max-width: 100%; }
`;
const FULL_WIDTH_STYLE = `
  html, body { margin: 0; padding: 0; }
  iframe { display: block; width: 100% !important; max-width: 100%; border: 0; }
`;

// The pasted HTML as a document of its own: a whole page keeps its head
// (our styles go first, so its own win), a fragment gets one
const toDocument = (html, fullWidth) => {
  const head = `<meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><base target="_blank"><style>${BASE_STYLE}${
    fullWidth ? FULL_WIDTH_STYLE : ''
  }</style>`;
  const source = html || '';
  if (/<head[^>]*>/i.test(source)) {
    return source.replace(/<head[^>]*>/i, (tag) => `${tag}${head}`);
  }
  if (/<html[^>]*>/i.test(source)) {
    return source.replace(
      /<html[^>]*>/i,
      (tag) => `${tag}<head>${head}</head>`
    );
  }
  return `<!doctype html><html><head>${head}</head><body>${source}</body></html>`;
};

/**
 * The HTML in an iframe component (or an AI page), shown in the preview as
 * the app shows it: in a frame of its own as wide as the phone's screen, so
 * its media queries, vw units and widths respond to the phone and not to
 * the admin's window. The frame grows to fit the page. Its styles stay in
 * the frame, and, as before, script tags in it don't run (the sandbox has
 * no allow-scripts).
 */
const EmbedPreview = ({ html, fullWidth, className }) => {
  const boxRef = useRef(null);
  const frameRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const srcDoc = useMemo(() => toDocument(html, fullWidth), [html, fullWidth]);

  // The space it gets, up to a phone's width. The frame's new height
  // resizes this box too, so the width is set a frame later and only when
  // it changed: setting it in the callback itself sets off "ResizeObserver
  // loop completed with undelivered notifications"
  useEffect(() => {
    const box = boxRef.current;
    if (!box || typeof ResizeObserver === 'undefined') {
      setWidth(PHONE_WIDTH);
      return undefined;
    }
    let frameId;
    const observer = new ResizeObserver(([entry]) => {
      const next = Math.min(Math.floor(entry.contentRect.width), PHONE_WIDTH);
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() =>
        setWidth((current) => (current === next ? current : next))
      );
    });
    observer.observe(box);
    return () => {
      cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, []);

  // The page's height: once it has loaded (with its images), when its fonts
  // are in, and again at a new width (it reflows). Nothing observes inside
  // the frame: the frame's own height would feed back into it
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;
    let frameId;
    const measure = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        const doc = frame.contentDocument;
        if (!doc?.body) return;
        const { marginTop, marginBottom } = doc.defaultView.getComputedStyle(
          doc.body
        );
        // The body with its margins, or the whole page when a child's
        // margin collapses through the body (whichever is taller)
        const next = Math.ceil(
          Math.max(
            doc.body.getBoundingClientRect().height +
              parseFloat(marginTop) +
              parseFloat(marginBottom),
            doc.documentElement.getBoundingClientRect().height
          )
        );
        setHeight((current) => (current === next ? current : next));
      });
    };
    const onLoad = () => {
      measure();
      frame.contentDocument?.fonts?.ready.then(measure);
    };
    frame.addEventListener('load', onLoad);
    // Already loaded before this ran, or only the width changed
    if (frame.contentDocument?.readyState === 'complete') onLoad();
    return () => {
      cancelAnimationFrame(frameId);
      frame.removeEventListener('load', onLoad);
    };
  }, [srcDoc, width]);

  return (
    <div ref={boxRef} className={className}>
      <iframe
        ref={frameRef}
        title="preview"
        className="embed-preview"
        srcDoc={srcDoc}
        // Same origin only to measure it; no scripts
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        scrolling="no"
        style={{ width: width || '100%', height: height || 40 }}
      />
    </div>
  );
};

EmbedPreview.propTypes = {
  html: PropTypes.string,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
};

EmbedPreview.defaultProps = {
  html: '',
  fullWidth: false,
  className: undefined,
};

export default EmbedPreview;
